import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";
import {
  DEFAULT_LOAN_TERM_MONTHS,
  legalPersonKindSchema,
  type LoanAgreement,
} from "@/contracts";

import {
  createLoanAgreementMutation,
  deviceOptionsQuery,
  organizationOptionsQuery,
  siteOptionsQuery,
  updateLoanAgreementMutation,
} from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";
import { formatDevEui } from "@/lib/format";
import { LEGAL_PERSON_LABELS } from "./loan-labels";

const formSchema = z.object({
  organizationId: z.string(),
  siteId: z.string().min(1, "Choose the site where the equipment goes."),
  borrowerKind: legalPersonKindSchema,
  borrowerName: z.string().trim().min(2, "Enter the borrower's full legal name.").max(200),
  borrowerTaxId: z.string().trim().min(1, "Enter the CUIT or CUIL.").max(40),
  borrowerAddress: z.string().trim().min(5, "Enter the borrower's address.").max(300),
  signatoryName: z.string().trim().min(2, "Enter who will sign.").max(200),
  signatoryDocument: z.string().trim().min(1, "Enter the signatory's ID document.").max(40),
  signatoryCapacity: z.string().trim().min(2, "State what entitles them to sign.").max(120),
  purpose: z.string().trim().min(5, "State what the equipment is for.").max(500),
  // A string, like every other control: `z.coerce` would make the schema's
  // input and output types differ, and the resolver is typed on one shape.
  termMonths: z
    .string()
    .trim()
    .refine(
      (value) => Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 120,
      {
        message: "Between 1 and 120 months.",
      },
    ),
  startsOn: z.string().min(1, "Choose a start date."),
  deviceIds: z.array(z.string()).min(1, "Choose at least one device to lend."),
  notes: z.string().trim().max(2_000),
});

type FormValues = z.infer<typeof formSchema>;

function valuesFromAgreement(agreement: LoanAgreement): FormValues {
  return {
    organizationId: agreement.organizationId,
    siteId: agreement.siteId ?? "",
    borrowerKind: agreement.borrowerKind,
    borrowerName: agreement.borrowerName,
    borrowerTaxId: agreement.borrowerTaxId,
    borrowerAddress: agreement.borrowerAddress,
    signatoryName: agreement.signatoryName,
    signatoryDocument: agreement.signatoryDocument,
    signatoryCapacity: agreement.signatoryCapacity,
    purpose: agreement.purpose,
    termMonths: String(agreement.termMonths),
    startsOn: agreement.startsOn.slice(0, 10),
    deviceIds: agreement.items.map((item) => item.deviceId),
    notes: agreement.notes ?? "",
  };
}

const emptyDefaults: FormValues = {
  organizationId: "",
  siteId: "",
  borrowerKind: "LEGAL_ENTITY",
  borrowerName: "",
  borrowerTaxId: "",
  borrowerAddress: "",
  signatoryName: "",
  signatoryDocument: "",
  signatoryCapacity: "",
  purpose: "Monitoreo de temperatura",
  termMonths: String(DEFAULT_LOAN_TERM_MONTHS),
  startsOn: new Date().toISOString().slice(0, 10),
  deviceIds: [],
  notes: "",
};

/**
 * Drawing up or editing a draft.
 *
 * Long for a dialog, and the length is the point: every field here ends up
 * printed on an instrument somebody signs, and asking for the signatory's
 * capacity at the moment the contract is drawn up is cheaper than
 * discovering afterwards that a shop assistant signed for a company.
 *
 * Site and organisation stay fixed on edit — the API will not move a draft
 * onto another location.
 */
export function LoanAgreementCreateDialog({
  open,
  onClose,
  agreement,
}: {
  open: boolean;
  onClose: () => void;
  /** When set, the dialog edits this draft instead of creating a new one. */
  agreement?: LoanAgreement;
}) {
  const client = useApiClient();
  const { t } = useTranslation("loans");
  const { t: tCommon } = useTranslation("common");
  const { t: tPages } = useTranslation("pages");
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const editing = agreement !== undefined;

  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const organizations = useQuery({ ...organizationOptionsQuery(client), enabled: isSuperAdmin && !editing });

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: emptyDefaults,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(agreement ? valuesFromAgreement(agreement) : emptyDefaults);
  }, [open, agreement, reset]);

  const organizationId = useWatch({ control, name: "organizationId" });
  const siteId = useWatch({ control, name: "siteId" });
  const borrowerKind = useWatch({ control, name: "borrowerKind" });

  const sites = useQuery({
    ...siteOptionsQuery(client, organizationId || undefined),
    enabled: !editing,
  });
  // The equipment list is scoped to the chosen site, because that is what the
  // contract says about where it lives.
  const devices = useQuery({
    ...deviceOptionsQuery(
      client,
      siteId,
      editing ? agreement.organizationId : organizationId || undefined,
    ),
    enabled: siteId !== "",
  });

  const createMutation = useMutation({
    ...createLoanAgreementMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["loan-agreements"] });
    },
  });

  const updateMutation = useMutation({
    ...updateLoanAgreementMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["loan-agreements"] });
    },
  });

  const mutation = editing ? updateMutation : createMutation;

  const onSubmit = handleSubmit(async (values) => {
    if (editing) {
      await updateMutation.mutateAsync({
        id: agreement.id,
        body: {
          borrowerKind: values.borrowerKind,
          borrowerName: values.borrowerName,
          borrowerTaxId: values.borrowerTaxId,
          borrowerAddress: values.borrowerAddress,
          signatoryName: values.signatoryName,
          signatoryDocument: values.signatoryDocument,
          signatoryCapacity: values.signatoryCapacity,
          purpose: values.purpose,
          termMonths: Number(values.termMonths),
          startsOn: values.startsOn,
          deviceIds: values.deviceIds,
          notes: values.notes,
        },
      });
      reset();
      onClose();
      return;
    }

    const created = await createMutation.mutateAsync({
      ...(values.organizationId ? { organizationId: values.organizationId } : {}),
      siteId: values.siteId,
      borrowerKind: values.borrowerKind,
      borrowerName: values.borrowerName,
      borrowerTaxId: values.borrowerTaxId,
      borrowerAddress: values.borrowerAddress,
      signatoryName: values.signatoryName,
      signatoryDocument: values.signatoryDocument,
      signatoryCapacity: values.signatoryCapacity,
      purpose: values.purpose,
      termMonths: Number(values.termMonths),
      startsOn: values.startsOn,
      deviceIds: values.deviceIds,
      ...(values.notes ? { notes: values.notes } : {}),
    });

    reset();
    onClose();
    // Straight to the draft: the next thing anybody wants is to read what it
    // will say before issuing it.
    await navigate(`/loan-agreements/${created.id}`);
  });

  const error = (field: keyof FormValues) => {
    const message = errors[field]?.message;

    return message === undefined ? {} : { error: message };
  };

  return (
    <FormDialog
      open={open}
      title={editing ? t("editTitle") : t("createTitle")}
      description={editing ? t("editDescription") : t("createDescription")}
      submitLabel={editing ? t("editSubmit") : t("createSubmit")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={() => {
        reset();
        onClose();
      }}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <Alert tone="info" title={t("createWhatTitle")} announce={false}>
          {t("createWhatBody")}
        </Alert>

        {editing ? (
          <p className="text-sm text-ink-muted">
            {t("editSiteLocked", { site: agreement.siteName })}
          </p>
        ) : (
          <>
            {isSuperAdmin ? (
              <SelectField
                label={tCommon("organization")}
                required
                value={organizationId}
                onChange={(value) =>
                  setValue("organizationId", value, { shouldValidate: true, shouldDirty: true })
                }
                data={[
                  { value: "", label: tPages("chooseOrganization") },
                  ...(organizations.data?.data ?? []).map((organization) => ({
                    value: organization.id,
                    label: organization.name,
                  })),
                ]}
              />
            ) : null}

            <SelectField
              label={tCommon("site")}
              required
              {...error("siteId")}
              value={siteId}
              onChange={(value) =>
                setValue("siteId", value, { shouldValidate: true, shouldDirty: true })
              }
              data={[
                { value: "", label: "Choose a site" },
                ...(sites.data?.data ?? []).map((site) => ({
                  value: site.id,
                  label: `${site.name}${site.address ? ` — ${site.address}` : " (no address yet)"}`,
                })),
              ]}
            />
          </>
        )}

        <fieldset className="flex flex-col gap-3 rounded-lg border border-border-subtle p-3">
          <legend className="px-1 text-xs font-semibold text-ink-muted">{t("legendBorrower")}</legend>

          <SelectField
            label={t("fieldBorrowerIs")}
            value={borrowerKind}
            onChange={(value) =>
              setValue("borrowerKind", value as FormValues["borrowerKind"], {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            data={Object.entries(LEGAL_PERSON_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />

          <TextField
            label={t("fieldLegalName")}
            required
            {...error("borrowerName")}
            {...register("borrowerName")}
          />
          <TextField
            label={t("fieldCuitCuil")}
            required
            {...error("borrowerTaxId")}
            {...register("borrowerTaxId")}
          />
          <TextField
            label={t("fieldAddress")}
            required
            {...error("borrowerAddress")}
            {...register("borrowerAddress")}
          />
        </fieldset>

        <fieldset className="flex flex-col gap-3 rounded-lg border border-border-subtle p-3">
          <legend className="px-1 text-xs font-semibold text-ink-muted">{t("legendWhoSigns")}</legend>

          <TextField
            label={t("fieldSignerName")}
            required
            {...error("signatoryName")}
            {...register("signatoryName")}
          />
          <TextField
            label={t("fieldIdDocument")}
            hint={t("hintIdDocument")}
            required
            {...error("signatoryDocument")}
            {...register("signatoryDocument")}
          />
          <TextField
            label={t("fieldCapacity")}
            hint={t("hintCapacity")}
            required
            {...error("signatoryCapacity")}
            {...register("signatoryCapacity")}
          />
        </fieldset>

        <TextField
          label={t("fieldPurpose")}
          hint={t("hintPurpose")}
          required
          {...error("purpose")}
          {...register("purpose")}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label={t("fieldStartsOn")}
            type="date"
            required
            {...error("startsOn")}
            {...register("startsOn")}
          />
          <TextField
            label={t("fieldTermMonths")}
            type="number"
            min={1}
            max={120}
            required
            {...error("termMonths")}
            {...register("termMonths")}
          />
        </div>

        <fieldset className="flex flex-col gap-2 rounded-lg border border-border-subtle p-3">
          <legend className="px-1 text-xs font-semibold text-ink-muted">{t("legendEquipment")}</legend>

          {siteId === "" ? (
            <p className="text-sm text-ink-muted">{t("chooseSiteFirst")}</p>
          ) : (devices.data?.data ?? []).length === 0 ? (
            <p className="text-sm text-ink-muted">{t("noDevicesAtSite")}</p>
          ) : (
            (devices.data?.data ?? []).map((device) => (
              <label key={device.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  value={device.id}
                  className="size-4 rounded border-border accent-brand"
                  {...register("deviceIds")}
                />
                <span className="font-mono text-xs">{formatDevEui(device.devEui)}</span>
                <span className="text-ink-muted">
                  {device.manufacturer} {device.model}
                </span>
              </label>
            ))
          )}

          {errors.deviceIds?.message ? (
            <p className="text-sm text-danger">{errors.deviceIds.message}</p>
          ) : null}

          <p className="text-xs text-ink-muted">{t("gatewaysNotLent")}</p>
        </fieldset>

        <TextAreaField
          label={t("fieldInternalNotes")}
          hint={t("hintInternalNotes")}
          {...error("notes")}
          {...register("notes")}
        />
      </div>
    </FormDialog>
  );
}
